import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Menu, Progress } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { styled } from 'styled-components';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  ScheduleOutlined,
  MailOutlined,
  LikeOutlined,
  BarChartOutlined,
  SettingOutlined,
  ShopOutlined,
  ArrowRightOutlined,
  CarryOutOutlined,
  ApartmentOutlined,
  TeamOutlined,
  BlockOutlined,
  UserOutlined,
  UnorderedListOutlined,
  NotificationOutlined,
  CommentOutlined,
  FieldTimeOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  FundProjectionScreenOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { isTrialPhasePromoActive } from '../../shared/utils';
import { API_ENV } from '../../constants';
import { ProgressModal } from './ProgressModal';
import { SidebarLogo } from './SidebarLogo';
import { changeLocation } from '../../actions';
import { t } from '../../utils/i18n';
import EVENT_BUS_EVENTS from '../../constants/event-bus-events';
import { buildScreenRouteMapping } from '../../constants/screen-routes';
import { useGlobals } from '../../globals-context';
import createQueryConfig from '../../queries';

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  background: white;
  border-right: none;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }

  .ant-menu {
    background: transparent;
    border-inline-end: none;
    padding: 0 12px;
  }

  .ant-menu-item,
  .ant-menu-submenu-title {
    height: 44px;
    line-height: 44px;
    border-radius: 8px;
    margin: 4px 0;
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .ant-menu-item-selected {
    background: #e8f8fe !important;
    color: #00c2d1 !important;

    .anticon {
      color: #00c2d1;
    }
  }

  .ant-menu-item:hover:not(.ant-menu-item-disabled),
  .ant-menu-submenu-title:hover {
    background: #e8e8e8;
  }

  .ant-menu-item-disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }

  .ant-menu-submenu-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #8c8c8c;
  }

  .ant-menu-sub {
    background: transparent !important;
  }

  .ant-menu-sub .ant-menu-item {
    padding-left: 48px !important;
    height: 40px;
    line-height: 40px;
  }

  .ant-layout-sider-trigger {
    display: none;
  }
`;

const StyledProgressCard = styled.div<{ collapsed?: boolean }>`
  margin: 16px;
  padding: ${({ collapsed }) => (collapsed ? '12px' : '10px')};
  height: ${({ collapsed }) => (collapsed ? '46px' : 'auto')};
  background: #f5f5f5;
  box-shadow: 0 6px 14px rgba(8, 18, 37, 0.06);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const ContentArea = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 12px;
  overflow: hidden;
  max-width: 0;
  opacity: 0;
  transform: translateX(-6px);
  transition:
    max-width 260ms cubic-bezier(0.2, 0.9, 0.23, 1),
    opacity 220ms ease,
    transform 260ms cubic-bezier(0.2, 0.9, 0.23, 1);

  &.expanded {
    max-width: 999px;
    opacity: 1;
    transform: translateX(0);
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 16px;
  padding-left: 5px;
  padding-top: 6px;
  font-weight: 600;
  line-height: 1;
  color: #121212;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #6b6b6b;
  line-height: 1.35;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;

  opacity: 0;
  transform: translateX(-4px);
  transition:
    opacity 200ms ease 60ms,
    transform 200ms ease 60ms;

  &.expanded {
    opacity: 1;
    transform: translateX(0);
  }

  svg {
    font-size: 16px;
    color: #9a9a9a;
  }
`;
type MenuItem = Required<MenuProps>['items'][number];

interface SideMenuProps {
  collapsed: boolean;
  screenName: string;
}

function ProgressCard({
  collapsed,
  onOpenProgressModal,
}: {
  collapsed: boolean;
  onOpenProgressModal: () => void;
}) {
  return (
    <StyledProgressCard collapsed={collapsed}>
      <Row>
        <Progress
          type="circle"
          percent={67}
          size={20}
          strokeColor="#00c2d1"
          style={{ marginBottom: 38 }}
          format={() => null}
        />
        <ContentArea
          className={collapsed ? 'collapsed' : 'expanded'}
          aria-hidden={collapsed}
        >
          <LeftGroup>
            <TextColumn>
              <Title>Your Progress</Title>
              <Subtitle>
                You&apos;ve completed 4/6 tasks. Keep on going!
              </Subtitle>
            </TextColumn>
          </LeftGroup>

          <IconWrap
            className={collapsed ? 'collapsed' : 'expanded'}
            onClick={(e) => {
              e.stopPropagation();
              onOpenProgressModal();
            }}
          >
            <ArrowRightOutlined size={3} style={{ marginTop: 26 }} />
          </IconWrap>
        </ContentArea>
      </Row>
    </StyledProgressCard>
  );
}

const getBookingPageUrl = (slug: string, locale: string) => {
  const baseUrl =
    API_ENV === 'production'
      ? 'https://connect.shore.com'
      : 'https://staging-connect.shore.com';
  return `${baseUrl}/bookings/${slug}/locations?locale=${locale}`;
};

export function SideMenu({ collapsed, screenName }: SideMenuProps) {
  const dispatch = useDispatch();
  const { queryClient, apiClient, eventBus } = useGlobals() as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { merchantSettings } = useMerchantSettings();
  const { company, trialDaysLeft } = merchantSettings || {};

  const currentMerchant = useSelector((state: any) => state.currentMerchant);
  const currentAccount = useSelector((state: any) => state.currentAccount);

  const merchantQuery = currentMerchant?.id
    ? queryConfig.merchantV2(currentMerchant.id)
    : null;

  const { data: merchantData } = useQuery({
    ...merchantQuery,
    enabled: Boolean(merchantQuery),
  }) as any;
  const sessionPermissions = useSelector(
    (state: any) => state.sessionPermissions
  );
  const accountLocale = currentAccount?.locale || 'en';
  const isTrialUser = isTrialPhasePromoActive({
    companyName: company,
    trialDaysLeft,
    roles: currentAccount?.roles || [],
  });

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [isProgressModalOpen, setProgressModalOpen] = useState(false);

  const [openKeys, setOpenKeys] = useLocalStorageState<string[]>(
    'as:menuOpenKeys',
    {
      defaultValue: [],
      listenStorageChange: false,
    }
  );

  const isInitialized = useRef(false);
  const prevScreenName = useRef('');
  const prevCollapsed = useRef(collapsed);

  const managerUrl =
    API_ENV === 'production'
      ? 'https://manager.shore.com/'
      : 'https://manager-staging.shore.com/';
  const bookingPageUrl = getBookingPageUrl(merchantData?.slug, accountLocale);
  const screenRouteMapping = useMemo(
    () => buildScreenRouteMapping({ bookingPageUrl, managerUrl }),
    [bookingPageUrl, managerUrl]
  );

  const menuItems: MenuItem[] = useMemo(
    () => [
      ...(sessionPermissions?.calendar?.available === true
        ? [
            {
              key: '/calendar',
              icon: <CalendarOutlined />,
              label: t('side-menu.calendar'),
            },
          ]
        : []),
      {
        key:
          API_ENV === 'production'
            ? 'https://manager.shore.com/'
            : 'https://manager-staging.shore.com/',
        icon: <ShoppingCartOutlined />,
        label: (
          <span>
            {t('side-menu.shore-pos')}{' '}
            <ExportOutlined style={{ fontSize: 12, marginLeft: 4 }} />
          </span>
        ),
      },
      ...(merchantData?.slug
        ? [
            {
              key: bookingPageUrl,
              icon: <ShopOutlined />,
              label: <span>{t('side-menu.booking-page')} </span>,
            },
          ]
        : []),
      ...(sessionPermissions?.shiftplan?.available === true && !isTrialUser
        ? [
            {
              key: '/shiftplan',
              icon: <CarryOutOutlined />,
              label: <span>{t('side-menu.shift-plan')}</span>,
            },
          ]
        : []),
      {
        key: 'management',
        icon: <ApartmentOutlined />,
        label: t('side-menu.management'),
        children: [
          ...(sessionPermissions?.customers?.available === true
            ? [
                {
                  key: '/customers',
                  icon: <TeamOutlined />,
                  label: t('side-menu.customers'),
                },
              ]
            : []),
          ...(sessionPermissions?.resources?.available === true
            ? [
                {
                  key: '/resources',
                  icon: <BlockOutlined />,
                  label: t('side-menu.resources'),
                },
              ]
            : []),
          ...(sessionPermissions?.hr?.available === true
            ? [
                {
                  key: '/hr',
                  icon: <UserOutlined />,
                  label: t('side-menu.employees'),
                },
              ]
            : []),

          ...(sessionPermissions?.services?.available === true
            ? [
                {
                  key: '/services',
                  icon: <UnorderedListOutlined />,
                  label: t('side-menu.services'),
                },
              ]
            : []),
        ],
      },
      {
        key: 'marketing',
        icon: <NotificationOutlined />,
        label: t('side-menu.marketing'),
        children: [
          ...(sessionPermissions?.messenger?.available === true && !isTrialUser
            ? [
                {
                  key: '/services#/messenger',
                  icon: <CommentOutlined />,
                  label: t('side-menu.messenger'),
                },
              ]
            : []),
          ...(sessionPermissions?.newsletter?.available === true && !isTrialUser
            ? [
                {
                  key: '/newsletter',
                  icon: <MailOutlined />,
                  label: t('side-menu.newsletter'),
                },
              ]
            : []),
          ...(sessionPermissions?.feedback?.available === true
            ? [
                {
                  key: '/feedback',
                  icon: <LikeOutlined />,
                  label: t('side-menu.feedback'),
                },
              ]
            : []),
          ...(sessionPermissions?.reminders?.available === true
            ? [
                {
                  key: '/reminders',
                  icon: <FieldTimeOutlined />,
                  label: t('side-menu.reminders'),
                },
              ]
            : []),
        ],
      },
      {
        key: 'analysis',
        icon: <BarChartOutlined />,
        label: t('side-menu.analysis'),
        children: [
          ...(sessionPermissions?.insights?.available === true
            ? [
                {
                  key: '/insights',
                  icon: <DashboardOutlined />,
                  label: t('side-menu.dashboard'),
                },
              ]
            : []),
          ...(sessionPermissions?.transactions?.available === true
            ? [
                {
                  key: '/transactions',
                  icon: <CreditCardOutlined />,
                  label: t('side-menu.transactions'),
                },
              ]
            : []),
          ...(sessionPermissions?.reportings?.available === true
            ? [
                {
                  key: '/pos-reports-v2',
                  icon: <FundProjectionScreenOutlined />,
                  label: t('side-menu.reports'),
                },
              ]
            : []),
        ],
      },
      {
        key: 'setup',
        icon: <SettingOutlined />,
        label: t('side-menu.setup'),
        children: [
          {
            key: '/application-settings',
            icon: <ScheduleOutlined />,
            label: t('side-menu.calendar-settings'),
          },
          ...(sessionPermissions?.settings?.available === true
            ? [
                {
                  key: '/settings',
                  icon: <ToolOutlined />,
                  label: t('side-menu.company-settings'),
                },
              ]
            : []),
          {
            key: '/locations',
            icon: <EnvironmentOutlined />,
            label: t('side-menu.location-settings'),
          },
        ],
      },
    ],
    [
      sessionPermissions,
      accountLocale,
      bookingPageUrl,
      isTrialUser,
      dispatch,
      eventBus,
    ]
  );

  useEffect(() => {
    const mapping = screenRouteMapping[screenName];

    if (!mapping) {
      return;
    }

    if (prevScreenName.current === screenName && isInitialized.current) {
      return;
    }
    prevScreenName.current = screenName;
    const newSelectedKey = mapping.key;

    if (selectedKey !== newSelectedKey) {
      setSelectedKey(newSelectedKey);
    }

    // Update openKeys based on parent, but only if not collapsed
    // Don't re-trigger if only collapsed changed
    if (!collapsed && mapping.parent) {
      const newOpenKeys = [mapping.parent];
      if (JSON.stringify(openKeys) !== JSON.stringify(newOpenKeys)) {
        setOpenKeys(newOpenKeys);
      }
    } else if (collapsed && openKeys && openKeys.length > 0) {
      // Clear openKeys when collapsed
      setOpenKeys([]);
    }

    isInitialized.current = true;
    prevCollapsed.current = collapsed;
    // eslint-disable-next-line
  }, [screenRouteMapping, screenName, collapsed]);

  const handleOpenChange = (keys: string[]) => {
    if (!keys || keys.length === 0) {
      setOpenKeys([]);
      return;
    }
    const latest = keys[keys.length - 1];
    setOpenKeys(latest ? [latest] : []);
  };

  const handleOnMenuClick: MenuProps['onClick'] = (e) => {
    const menuItem = menuItems.find((item: any) => item?.key === e.key);
    if (menuItem && (menuItem as any).disabled) {
      return;
    }

    if (e.key.startsWith('http://') || e.key.startsWith('https://')) {
      window.open(e.key, '_blank');
      return;
    }

    if (e.key.startsWith('/')) {
      const route = e.key;
      setSelectedKey(e.key);
      dispatch(changeLocation(route));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  return (
    <StyledSider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={260}
      collapsedWidth={80}
    >
      <SidebarLogo collapsed={collapsed} />
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleOnMenuClick}
      />

      {/* <ProgressCard
        collapsed={collapsed}
        onOpenProgressModal={() => setProgressModalOpen(true)}
      /> */}

      {/* {isProgressModalOpen && (
        <ProgressModal
          open={isProgressModalOpen}
          onClose={() => setProgressModalOpen(false)}
        />
      )} */}
    </StyledSider>
  );
}
